import { useEffect, useRef, useState } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import type { SettingsFlow } from '@ory/client'

import { fetchSettingsFlow, fetchWhoami } from '@utils/kratos.utils'
import { logClientError, translateClientError } from '@i18n/kratos/kratos-client-error'
import { assertNever } from '@utils/assert.utils'
import { findInput } from '@utils/kratos-nodes.utils'
import { isNonScriptUrl, isSameOriginFormAction } from '@utils/url.utils'

// Preserve return_to through the login round-trip — otherwise the user lands
// back on /settings without it and the Back link / auto-redirect silently die.
const redirectToLogin = (returnTo: string | null): void => {
  const innerSettings =
    returnTo && isNonScriptUrl(returnTo)
      ? `${window.location.origin}/settings?return_to=${encodeURIComponent(returnTo)}`
      : `${window.location.origin}/settings`
  window.location.replace(
    `/self-service/login/browser?return_to=${encodeURIComponent(innerSettings)}`,
  )
}

// Forward `?return_to=` so Kratos puts it on `flow.return_to` for later redirect.
const settingsBrowserInitUrl = (returnTo: string | null): string => {
  if (returnTo && isNonScriptUrl(returnTo)) {
    return `/self-service/settings/browser?return_to=${encodeURIComponent(returnTo)}`
  }
  return '/self-service/settings/browser'
}

interface State {
  flow: SettingsFlow | null
  csrfValue: string
  error: string | null
  loading: boolean
}

const INITIAL_STATE: State = {
  flow: null,
  csrfValue: '',
  error: null,
  loading: true,
}

type ValidationResult =
  | { kind: 'ok'; flow: SettingsFlow; csrfValue: string }
  | { kind: 'error'; message: string }

// Validation lives in the fetch path so a non-null `flow` is always
// renderable — callers don't need to derive a parallel "validated" flag and
// gate every effect on it. csrfValue rides along so consumers don't repeat
// the findInput lookup.
const validateFlow = (flow: SettingsFlow, intl: IntlShape): ValidationResult => {
  const csrfAttrs = findInput(flow.ui.nodes, 'csrf_token')
  if (!csrfAttrs) {
    return {
      kind: 'error',
      message: intl.formatMessage({ id: 'settings.error.csrfUnavailable' }),
    }
  }
  if (!isSameOriginFormAction(flow.ui.action)) {
    return {
      kind: 'error',
      message: intl.formatMessage({ id: 'settings.error.formActionUnexpectedOrigin' }),
    }
  }
  return { kind: 'ok', flow, csrfValue: String(csrfAttrs.value ?? '') }
}

export const useSettingsFlow = (flowId: string | null, returnTo: string | null): State => {
  const intl = useIntl()
  const [state, setState] = useState<State>(INITIAL_STATE)

  // intl is a fresh IntlShape on every LanguageSwitcher click. Putting it
  // directly in the effect's dep array would re-fetch the same flow on
  // every locale toggle; reading through the ref also gives async resolves
  // below the locale the user is reading *now*, not the one captured at
  // fetch-start. Sync via useEffect — concurrent-render-safe.
  const intlRef = useRef(intl)
  useEffect(() => {
    intlRef.current = intl
  }, [intl])

  useEffect(() => {
    // Kratos full-redirects on every flow change today so this fires from
    // INITIAL_STATE anyway, but a future client-side navigation between
    // /settings?flow=A and /settings?flow=B would otherwise keep rendering
    // the stale flow until the new fetch resolves.
    setState(INITIAL_STATE)

    // whoami first: without a privileged session, /self-service/settings/browser
    // 303s into default_browser_return_url which bounces back here → loop.
    if (!flowId) {
      const controller = new AbortController()
      fetchWhoami(controller.signal).then((result) => {
        if (controller.signal.aborted) {
          return
        }
        switch (result.kind) {
          case 'authenticated':
            window.location.replace(settingsBrowserInitUrl(returnTo))
            break
          case 'anonymous':
            redirectToLogin(returnTo)
            break
          case 'error':
            logClientError(result.code, result.message)
            setState({
              flow: null,
              csrfValue: '',
              error: translateClientError(intlRef.current, result.code),
              loading: false,
            })
            break
          default:
            assertNever(result)
        }
      })
      return () => {
        controller.abort()
      }
    }

    const controller = new AbortController()
    fetchSettingsFlow(flowId, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return
      }
      switch (result.kind) {
        case 'ok': {
          const validation = validateFlow(result.flow, intlRef.current)
          if (validation.kind === 'ok') {
            setState({
              flow: validation.flow,
              csrfValue: validation.csrfValue,
              error: null,
              loading: false,
            })
          } else {
            setState({
              flow: null,
              csrfValue: '',
              error: validation.message,
              loading: false,
            })
          }
          break
        }
        case 'expired':
          window.location.replace(settingsBrowserInitUrl(returnTo))
          break
        case 'reauth_required':
          redirectToLogin(returnTo)
          break
        case 'error':
          logClientError(result.code, result.message)
          setState({
            flow: null,
            csrfValue: '',
            error: translateClientError(intlRef.current, result.code),
            loading: false,
          })
          break
        default:
          assertNever(result)
      }
    })
    return () => {
      controller.abort()
    }
  }, [flowId, returnTo])

  return state
}
